<template>
    <section class="filters">
        <div class="filters__heading flex flex--align-center flex--justify-space-between">
            <span>{{ $t('transactionHistoryDetails.filters') }}</span>

            <i :class="[
                   'icon',
                   {
                       'icon-cancel': shouldShowFilters,
                       'icon-filter-icon': !shouldShowFilters
                   }
               ]"
                @click="toggleFilters"
            />
        </div>

        <filters v-if="Object.keys(selectedFilters).length"
            storageKey="transactionsFilters"
            :selectedFilters="selectedFilters"
            :debounceTime="1000"
            @filtersChange="$emit('filtersRequest', $event)"
            @filtersInteraction="$emit('filtersInteraction')"
        >
            <template #default="{
                activeFilters,
                addFilters,
                getFilterDataById,
                removeFilterGroupById,
                removeFilter,
                resetAllFilters,
            }"
            >
                <div>
                    <div v-show="shouldShowFilters"
                        :class="['filters__wrapper flex', {'flex--equal-distribution': !isPartnerApp}]"
                    >
                        <!-- Date Picker -->
                        <div :class="['filters__date-picker', {'filters__date-picker-partner': isPartnerApp}]">
                            <date-range-picker
                                :default-range="getFilterDataById('transactionConfirmedAt')[0]"
                                @dateChanged="addFilters({
                                    id: 'transactionConfirmedAt',
                                    data: {
                                        text: getDateRangeText($event),
                                        startDate: formatDate($event.startDate),
                                        endDate: formatDate($event.endDate),
                                    },
                                    format: {
                                        'transactionConfirmedAt[from]': '{startDate}',
                                        'transactionConfirmedAt[to]': '{endDate}',
                                    }
                                })"
                            />
                        </div>

                        <!-- Custom Amount -->
                        <div class="custom-select">
                            <span class="custom-select__label custom-select__label">{{ $t('transactionHistoryDetails.amount') }}</span>

                            <div class="flex flex--column">
                                <custom-select class="custom-select__wrapper"
                                    :items="customAmounts"
                                    :selected="getFilterDataById('selectAmount')"
                                    :max-list-height="23"
                                    @selection="($event) => {
                                        removeFilterGroupById('inputAmount');
                                        addFilters({
                                            id: 'selectAmount',
                                            data: $event,
                                            format: {
                                                'transactionAmount[from]': '{value.min}',
                                                'transactionAmount[to]': '{value.max}',
                                            }
                                        });
                                    }"
                                />

                                <label class="custom-select__input-label">{{ $t('global.search.custom') }}</label>

                                <custom-range :selectedRange="getFilterDataById('inputAmount')[0]"
                                    @change="($event) => {
                                        removeFilterGroupById('selectAmount');
                                        addFilters({
                                            id: 'inputAmount',
                                            data: {
                                                ...$event.range,
                                                text: $event.text,
                                            },
                                            format: {
                                                'transactionAmount[from]': '{min}',
                                                'transactionAmount[to]': '{max}',
                                            }
                                        });
                                    }"
                                />
                            </div>
                        </div>

                        <div class="filters__seperator">
                            <span class="filters__seperator-inner" />
                        </div>

                        <!-- Batch Type -->
                        <div v-if="!isPartnerApp"
                            class="custom-select"
                        >
                            <span class="custom-select__label">{{ $t('transactionHistoryDetails.type') }}</span>

                            <custom-select class="custom-select__wrapper"
                                :items="batchTypes"
                                :selected="getFilterDataById('batchTypes')"
                                :max-list-height="23"
                                multi
                                @selection="addFilters({
                                    id: 'batchTypes',
                                    data: $event,
                                    format: {
                                        'batchTypes': '[{value}]',
                                    },
                                })"
                            />
                        </div>

                        <!-- Batch Statuses -->
                        <div v-if="!isPartnerApp"
                            class="custom-select"
                        >
                            <span class="custom-select__label">{{ $t('transactionHistoryDetails.status') }}</span>

                            <custom-select class="custom-select__wrapper"
                                :max-list-height="23"
                                :items="batchStatuses"
                                :selected="getFilterDataById('batchStatuses')"
                                multi
                                @selection="addFilters({
                                    id: 'batchStatuses',
                                    data: $event,
                                    format: {
                                        'batchStatuses': '[{value}]'
                                    }
                                })"
                            />
                        </div>

                        <!-- Currency Type (CHZ/FT) -->
                        <div class="custom-select">
                            <span class="custom-select__label">{{ $t('transactionHistoryDetails.currency') }}</span>

                            <custom-select class="custom-select__wrapper"
                                :items="types"
                                :selected="getFilterDataById('type')"
                                toggle
                                @selection="($event) => {
                                    if ($event[0].value === TransactionType.Main) {
                                        removeFilterGroupById('currencyShortCodes');
                                    }

                                    addFilters({
                                        id: 'type',
                                        data: $event,
                                        format: {
                                            'type': '{value}'
                                        }
                                    });
                                }"
                            />
                        </div>

                        <!-- Fan Token Currency -->
                        <div v-if="!isPartnerApp"
                            class="custom-select flex flex--column"
                        >
                            <span :class="['custom-select__label', { 'custom-select__label--disabled': isTypeMain(activeFilters) }]">
                                {{ $t('transactionHistoryDetails.token') }}
                            </span>

                            <custom-select class="custom-select__wrapper"
                                :filter="true"
                                :max-list-height="22"
                                :items="mappedCurrencies"
                                :selected="getFilterDataById('currencyShortCodes')"
                                :is-disabled="isTypeMain(activeFilters)"
                                multi
                                @selection="addFilters({
                                    id: 'currencyShortCodes',
                                    data: $event,
                                    format: {
                                        'currencyShortCodes': '[{value}]'
                                    }
                                })"
                            />
                        </div>
                    </div>

                    <!-- Active filters will always contain the currency type, so checking for length greater than 1 -->
                    <div v-if="activeFilters.length > 1 && !isPartnerApp"
                        class="selections flex flex--wrap"
                    >
                        <template v-for="(filter, i) in activeFilters">
                            <div v-if="filter.id !== 'type' && filter.data.text"
                                :key="i"
                                class="flex flex--wrap"
                            >
                                <div class="selections__selection flex flex--align-center flex--justify-center">
                                    <span>{{ filter.data.text }}</span>

                                    <i class="icon icon-cancel"
                                        @click="removeFilter(filter)"
                                    />
                                </div>
                            </div>
                        </template>

                        <button class="selections__reset-btn"
                            @click="() => {
                                resetAllFilters();
                                selectedFilters = { ...selectedMainTypeFilters };
                            }"
                        >
                            {{ $t('transactionHistoryDetails.reset') }}
                        </button>
                    </div>
                </div>
            </template>
        </filters>
    </section>
</template>

<script>
// 3rd Party
import {
    mapGetters,
} from 'vuex';

// Components
import dateRangePicker from '@webapp/components/filters/date-range-picker/date-range-picker';
import customSelect from '@webapp/components/filters/select';
import filters from '@shared/components/filters.vue';
import customRange from '@webapp/components/filters/custom-range.vue';

// Utils
import {
    format,
} from '@shared/utils/dates';

// Helpers
import {
    isNumber,
} from '@shared/utils/dom';

// Shared Types
import {
    TransactionType,
} from '@shared/config/transactions-types';

// Constants
import {
    filters as transactionFilters,
} from './transactions.const';

export default {
    components: {
        dateRangePicker,
        customSelect,
        filters,
        customRange,
    },
    props: {
        selectedTokenCode: {
            type: String,
            default: 'JUV',
        },
    },
    data() {
        return {
            shouldShowFilters: false,
            selectedFilters: {},
            TransactionType,
            customAmounts: transactionFilters.CUSTOM_AMOUNTS,
        };
    },
    computed: {
        ...mapGetters(['isPartnerApp']),

        ...mapGetters('wallet', ['isChzAllowed']),

        ...mapGetters('voteTokens', ['voteTokens']),

        selectedMainTypeFilters() {
            return {
                override: false,
                filters: [
                    {
                        id: 'type',
                        data: this.types.filter(({ value }) => value === TransactionType.Main),
                        format: {
                            'type': '{value}',
                        },
                    },
                ],
            };
        },

        selectedTokenCodeFilters() {
            return {
                override: true,
                filters: [
                    {
                        id: 'type',
                        data: this.types.filter(({ value }) => value === TransactionType.FanToken),
                        format: {
                            'type': '{value}',
                        },
                    },
                    {
                        id: 'currencyShortCodes',
                        data: this.mappedCurrencies.filter(({ value }) => value === this.selectedTokenCode),
                        format: {
                            'currencyShortCodes': '[{value}]',
                        },
                    },
                ],
            };
        },

        types() {
            // Returning hardcoded USD in case chz is not allowed. Change to preferred fiat currency name in case BE support it in the future
            return [
                {
                    text: this.isChzAllowed ? 'CHZ' : 'USD',
                    value: TransactionType.Main,
                },
                {
                    text: 'Fan Token',
                    value: TransactionType.FanToken,
                },
            ];
        },

        batchStatuses() {
            return transactionFilters.BATCH_STATUSES.map(({
                key,
                value,
            }) => ({
                text: this.$t(key),
                value,
            }));
        },

        batchTypes() {
            return transactionFilters.BATCH_TYPES.map(({
                key,
                value,
            }) => ({
                text: this.$t(key),
                value,
            }));
        },

        mappedCurrencies() {
            return this.voteTokens.map(({ shortCode }) => ({
                text: shortCode,
                value: shortCode,
            }));
        },
    },
    mounted() {
        this.selectedFilters = this.selectedTokenCode ? this.selectedTokenCodeFilters : this.selectedMainTypeFilters;
    },
    methods: {
        isNumber,

        toggleFilters() {
            this.shouldShowFilters = !this.shouldShowFilters;
        },

        isTypeMain(activeFilters) {
            const {
                value = '',
            } = activeFilters.find(({ id }) => id === 'type')?.data ?? {};

            return value === TransactionType.Main;
        },

        formatDate(date) {
            return format(new Date(date), 'standardDateTime');
        },

        getDateRangeText({
            startDate,
            endDate,
        }) {
            return `${format(new Date(startDate), 'dateShort')} - ${format(new Date(endDate), 'dateShort')}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.filters {
    &__heading {
        width: 10rem;
        height: 3.5rem;
        background-color: $white-color;
        border: 0.08rem solid $french-gray-color;
        border-radius: 0.4rem;
        padding: 0 1rem;
        box-sizing: border-box;

        span {
            font-family: "Nunito";
            font-size: 1.2rem;
            color: $cyprus-color;
        }
    }

    &__wrapper {
        height: 35rem;
        background-color: $white-color;
        padding: 1rem 3rem;
        margin-top: 1rem;
        border-radius: 0.4rem;
        box-sizing: border-box;
        gap: 3rem;
    }

    &__date-picker {
        flex: 1.5;
    }

    &__date-picker-partner {
        flex: 0;
    }

    &__seperator {
        height: 90%;
        align-self: center;
        position: relative;
        flex: 0.2;

        &-inner {
            width: 0.1rem;
            height: 100%;
            background-color: $dusty-gray-color;
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
        }
    }
}

.selections {
    margin-bottom: 1rem;

    &__selection {
        min-width: 5rem;
        height: 3.5rem;
        background: $athens-gray-color;
        border-radius: 0.4rem;
        border: 0.08rem solid $mercury-color;
        margin: 1rem 1rem 0 0;
        padding: 0 1rem;

        span {
            font-family: Nunito;
            font-size: 1.2rem;
            color: $cyprus-color;
            margin-right: 1.2rem;
        }
    }

    &__reset-btn {
        box-sizing: content-box;
        background: $blue-ribbon-color;
        outline: none;
        border: 0.08rem solid $blue-ribbon-color;
        color: $white-color;
        height: 3.5rem;
        width: 7.5rem;
        margin-top: 1rem;
        padding: 0;
        font-size: 1.2rem;
        border-radius: 0.4rem;
        cursor: pointer;
    }
}

.icon {
    font-size: 1.5rem;
    cursor: pointer;
}

.custom-select {
    position: relative;

    &__label {
        display: block;
        border-bottom: 0.1rem solid $dusty-gray-color;
        width: 100%;
        margin-top: 0.6rem;
        font-size: 1.1rem;
        font-weight: 600;
        padding-bottom: 0.4rem;

        &--disabled {
            opacity: 0.5;
        }
    }

    &__wrapper {
        margin-left: 0.2rem;
    }

    &__input-label {
        color: $dusty-gray-color;
        margin-bottom: 1rem;
        font-size: 1.2rem;
    }

    &__input {
        border: 0.08rem solid $athens-gray-color;
        font-family: "Nunito";
        border-radius: 0.4rem;
        width: 9.5rem;
        height: 3.5rem;
        padding-left: 0.5rem;
        margin-bottom: 1rem;

        &::placeholder {
            opacity: 0.5;
        }

        &--invalid {
            border-color: $torch-red-color;
        }
    }
}
</style>